import { CSSProperties, useMemo } from 'react';
import { Z_INDEX } from '@belong/themes';
import { ChatFrameSharedState, useIsParentLarge, useSendStateToParentFrame } from '@belong/chat-utils';

type IframeSize = 'full' | 'open' | 'closed' | 'closedLarge';

const Z_INDEX_ABOVE = Z_INDEX.GLOBAL_HEADER + 1;
const Z_INDEX_BELOW = Z_INDEX.GLOBAL_HEADER - 1;
const SIZE_STYLES: Record<IframeSize, CSSProperties> = {
  full: {
    width: '100%',
    height: '100%',
    // We always need the ChatDialog to be above the header is full screen
    zIndex: Z_INDEX_ABOVE
  },
  open: {
    width: 405, // ChatDialog width + 10px (left space) + 20px (right space)
    height: 676, // ChatDialog height + 10px (top space)
    maxWidth: '100%',
    maxHeight: 'calc(100vh - 10px)',
    // We always need the ChatDialog to be above the header when open
    zIndex: Z_INDEX_ABOVE
  },
  closed: {
    width: 155,
    height: 85,
    zIndex: Z_INDEX_BELOW
  },
  closedLarge: {
    width: 190,
    height: 105,
    zIndex: Z_INDEX_BELOW
  }
};

export interface IStyleGetterProps {
  isOpen?: boolean;
  isCovering?: boolean;
  isLargeViewport?: boolean;
}

export const getIframeStyles = ({ isOpen, isCovering, isLargeViewport }: IStyleGetterProps): CSSProperties => {
  const sizeStyles = ((): CSSProperties => {
    if (isCovering) {
      return SIZE_STYLES.full;
    }
    if (isOpen) {
      return SIZE_STYLES.open;
    }
    return isLargeViewport ? SIZE_STYLES.closedLarge : SIZE_STYLES.closed;
  })();

  return {
    border: 'none',
    position: 'fixed',
    bottom: 0,
    right: 0,
    ...sizeStyles
  };
};

export interface IUseUpdateChatIframe {
  isOpen?: boolean;
  isFullScreen?: boolean;
  modalVisible?: boolean;
}

export const useUpdateChatIframe = ({ isOpen, isFullScreen, modalVisible }: IUseUpdateChatIframe): void => {
  const isLargeViewport = useIsParentLarge();
  const sharedState: ChatFrameSharedState = useMemo(() => {
    const isCovering = (isOpen && isFullScreen) || modalVisible;
    const iframeStyles = getIframeStyles({ isOpen, isCovering, isLargeViewport });

    return {
      iframeStyles,
      isCovering
    };
  }, [isOpen, isFullScreen, modalVisible, isLargeViewport]);
  useSendStateToParentFrame(sharedState);
};
